import { addMonths, max, setDate, subYears } from 'date-fns'
import { List } from 'immutable'
import React, { ReactElement, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { usePrevious } from 'react-use'

import { addAlertSignature } from '../../../actions/alerts'
import { fetchEmployeeContractDeltas } from '../../../api/contracts'
import paths from '../../../constants/paths'
import { CoarseTimeRegistrationMutableFields } from '../../../model/coarseTimeRegistration'
import Contract from '../../../model/contract'
import ContractDelta from '../../../model/contractDelta'
import Employee from '../../../model/employee'
import SalaryCycle from '../../../model/salaryCycle'
import SalaryPeriod from '../../../model/salaryPeriod'
import SalaryType from '../../../model/salaryType'
import { DateFormat } from '../../../model/types'
import { CoarseTimeRegistrationReducer } from '../../../reducers/coarseTimeRegistrations'
import { ContractReducer } from '../../../reducers/contracts'
import { EmployeeContractDeltaReducer } from '../../../reducers/employeeContractDeltas'
import { regularComponentDidUpdate } from '../../../utils/component-utils'
import {
  formatAPIDate,
  formatDate,
  getDate,
  isSameOrAfter,
  isSameOrBefore,
  isTimeBefore,
} from '../../../utils/date-utils'
import { getValidFrom } from '../../../utils/employment-utils'
import { formatError } from '../../../utils/error-utils'
import { formatSavingText } from '../../../utils/loading-utils'
import { getCurrentPeriodFromDispositionDate } from '../../../utils/salary-period-utils'
import { t, tx } from '../../../utils/translation-utils'
import Form from '../../antd/form'
import Alert from '../../elements/alert'
import Col from '../../elements/grid/col'
import Row from '../../elements/grid/row'
import Select from '../../elements/select'
import LoadingOverlay from '../../widgets/LoadingOverlay'
import CoarseTimeRegistrationForm, { CoarseTimeRegistrationResult } from './CoarseTimeRegistrationForm'

type Props = {
  preventPeriodChange: boolean
  employee: Employee
  salaryPeriodID?: string
  contracts: ContractReducer
  employeeContractDeltas: EmployeeContractDeltaReducer
  coarseTimeRegistrations: CoarseTimeRegistrationReducer
  salaryCycles: List<SalaryCycle>
  salaryCycle: SalaryCycle
  salaryTypes: List<SalaryType>

  addAlert: addAlertSignature
  addContract: (contract: Contract) => void
  updateContract: (contract: Contract) => void
  updateCoarseTimeRegistrationBulk: (employeeID: string, registrations: CoarseTimeRegistrationMutableFields[]) => void
}

type ContractData = {
  validFrom: string
  validTo?: string
}

export default function CoarseTimeRegistrationTab(props: Props): ReactElement | null {
  const [salaryPeriodID, setSalaryPeriodID] = useState(() => {
    let periodID = props.salaryPeriodID
    if (!periodID) {
      periodID = getCurrentPeriodFromDispositionDate(props.salaryCycle.salaryPeriods)?.id
    }
    return periodID
  })
  const [error, setError] = useState<Error | null>(null)
  const [contractDelta, setContractDelta] = useState<ContractData[]>([])
  const [selectedContractID, setSelectedContractID] = useState<string>('')
  const [loadedDelta, setLoadedDelta] = useState<ContractDelta[]>([])

  const { coarseTimeRegistrations, addAlert } = props
  const previousCoarseTimeRegistrations = usePrevious(coarseTimeRegistrations)

  // Check if form2-to2 overlap with from1-to1
  function doPeriodOverlap(from1: string, to1: string, from2: string, to2: string): boolean {
    // if form2-to2 streach past from1-to1 on both side, it overlap
    if (isSameOrBefore(from2, from1) && isSameOrAfter(to2, to1)) {
      return true
    }
    // if from2 is inside form1-to1 it overlaps
    if (isSameOrAfter(from2, from1) && isSameOrBefore(from2, to1)) {
      return true
    }
    //  if to2 is inside form1-to1 it overlaps
    if (isSameOrAfter(to2, from1) && isSameOrBefore(to2, to1)) {
      return true
    }
    return false
  }

  useEffect(() => {
    fetchEmployeeContractDeltas(props.employee.id).then((res) => {
      setLoadedDelta(res.data)
    })
  }, [props.employee.id])

  useEffect(() => {
    // when the salay period is changed find the contract for valid ffor that period (if any)
    // if there are two contracts for the period, choose the newest.
    if (!salaryPeriodID) {
      return
    }
    type candidate = {
      id: string
      start: string
    }
    const candidates: candidate[] = []
    const period = props.salaryCycle.salaryPeriods.find((period) => period.id === salaryPeriodID)
    const start = period?.start || ''
    const end = period?.end || ''

    // finde all the contracts that are valid in the given period - these are the candidates
    for (const c of loadedDelta) {
      if (!c.validTo) {
        if (isSameOrBefore(c.validFrom, end)) {
          // possible candidate: It is the current contract and it is valid before the end of the period
          candidates.push({ id: c.contractID, start: c.validFrom })
        }
        continue
      }
      if (doPeriodOverlap(start, end, c.validFrom, c.validTo)) {
        // possible candidate: The contract valid start/end overlap with the period
        candidates.push({ id: c.contractID, start: c.validFrom })
        continue
      }
    }

    // order the candidate so the newest is first, then take that
    const ordered = candidates.sort().reverse()
    if (ordered.length != 0) {
      setSelectedContractID(ordered[0].id)
    }
  }, [salaryPeriodID, loadedDelta, props.salaryCycle.salaryPeriods])

  useEffect(() => {
    let contractData: ContractData[] = []
    const comparator = (a: ContractData, b: ContractData) => a.validFrom.localeCompare(b.validFrom)
    contractData = []
    for (const c of loadedDelta) {
      const elm: ContractData = {
        validFrom: c.validFrom,
        validTo: c.validTo ? c.validTo : undefined,
      }
      contractData.push(elm)
      contractData = contractData.sort(comparator)
      setContractDelta(contractData)
    }
  }, [loadedDelta])

  useEffect(() => {
    if (previousCoarseTimeRegistrations && previousCoarseTimeRegistrations.saving && !coarseTimeRegistrations.saving) {
      if (!coarseTimeRegistrations.error) {
        addAlert('success', t('time_registration_tab.coarse.alert.success'), { timeout: 5 })
      }
    }
  })

  const { contracts } = props

  useEffect(() => {
    regularComponentDidUpdate(contracts.error || coarseTimeRegistrations.error, error, setError)
  }, [contracts, coarseTimeRegistrations, error])

  type SalaryPeriodInfo = SalaryPeriod & {
    first: DateFormat
  }

  const getSalaryPeriods = (): SalaryPeriodInfo[] => {
    const contract = props.employee.activeContract
    if (!contract) {
      return []
    }

    let oldest = '1900-01-01'
    if (contractDelta.length > 0) {
      oldest = contractDelta[0].validFrom
    }

    let useFirst = false
    const salaryCycles = props.salaryCycles.filter(
      (item) => item.id === contract.salaryCycleID && item.frequency === 'Monthly'
    )
    if (salaryCycles.size > 0) {
      useFirst = true
    }
    return props.salaryCycle.salaryPeriods
      .map(
        (period): SalaryPeriodInfo => ({
          ...period,
          first: useFirst ? formatAPIDate(setDate(getDate(period.end), 1)) : period.start,
        })
      )
      .filter((salaryPeriod) => {
        // if salaryPeriod.end isBefore oldest then return false
        if (isTimeBefore(salaryPeriod.end, oldest)) {
          return false
        }
        const date = max([getDate(salaryPeriod.dispositionDate), getDate(salaryPeriod.end)])
        return isSameOrAfter(date, subYears(getDate(), 1)) && isSameOrBefore(date, addMonths(getDate(), 1))
      })
      .sort((a, b) => b.start.localeCompare(a.start))
  }

  const getPeriod = () => {
    return props.salaryCycle.salaryPeriods.find((salaryPeriod) => salaryPeriod.id === salaryPeriodID)
  }

  const handleSubmit = (values: CoarseTimeRegistrationResult) => {
    const period = getPeriod()
    if (!period) {
      return
    }
    const employee = props.employee
    if (employee.earliestMutableContract) {
      const contract = { ...employee.earliestMutableContract }
      if (contract.workSchedule !== values.workSchedule) {
        contract.workSchedule = values.workSchedule
        const { validFrom, canUpdate } = getValidFrom(employee, contract)
        if (canUpdate) {
          props.updateContract(contract)
        } else {
          contract.validFrom = formatAPIDate(validFrom)
          props.addContract(contract)
        }
      }
    }
    let workDays = values.workDays
    const registrations = Object.keys(values.workHours).reduce(
      (registrations: CoarseTimeRegistrationMutableFields[], salaryTypeID) => {
        const salaryClass = props.salaryTypes.find((type) => type.id === salaryTypeID)?.class
        const days = salaryClass && salaryClass === 'Hourly' ? workDays : 0
        if (days > 0) {
          workDays = 0 // so this is the only time it gets registered
        }
        registrations.push({
          employeeID: employee.id,
          salaryPeriodID: period.id,
          salaryTypeID: salaryTypeID,
          days: Math.ceil(days), // make sure it is always an integer
          hours: values.workHours[salaryTypeID],
        })
        return registrations
      },
      []
    )
    props.updateCoarseTimeRegistrationBulk(employee.id, registrations)
  }

  const getCoarseTimeRegistrations = () => {
    const period = getPeriod()
    if (!period) {
      return []
    }
    return props.coarseTimeRegistrations.coarseTimeRegistrations.filter(
      (registration) => registration.employeeID === props.employee.id && registration.salaryPeriodID === period.id
    )
  }
  const getWorkDays = () => {
    const registrations = getCoarseTimeRegistrations()
    let workDays = 0
    let hasWorkDays = false
    registrations.forEach((registration) => {
      workDays += registration.days
      hasWorkDays = true
    })
    return hasWorkDays ? workDays : undefined
  }
  const getWorkHours = () => {
    const workHours: Record<string, number> = {}
    const registrations = getCoarseTimeRegistrations()
    registrations.forEach((registration) => {
      workHours[registration.salaryTypeID] = registration.hours
    })
    return workHours
  }

  const period = getPeriod()
  const contract = props.employee.activeContract
  if (!period || !contract) {
    return null // probably shouldn't happen
  }
  return (
    <>
      {error && <Alert message={formatError(error)} type="error" showIcon />}
      {props.preventPeriodChange ? (
        <p>
          {t('time_registration_tab.coarse.prevent_period_change.line_1', {
            start: formatDate(period.start),
            end: formatDate(period.end),
          })}
          <br />
          {tx('time_registration_tab.coarse.prevent_period_change.line_2', {
            link: (
              <Link to={'/' + paths.EMPLOYEES + '/' + props.employee.id + '/time-registration'}>
                {t('time_registration_tab.coarse.prevent_period_change.line_2.link')}
              </Link>
            ),
          })}
        </p>
      ) : (
        <Row>
          <Col span={12}>
            <Form.Item style={{ marginBottom: 10 }}>
              <label style={{ marginBottom: 0 }}>{t('time_registration_tab.coarse.select_period')}</label>
              <Select
                value={salaryPeriodID}
                dropdownMatchSelectWidth={false}
                onChange={(e) => setSalaryPeriodID(e as string)}
              >
                {getSalaryPeriods().map((salaryPeriod) => {
                  return (
                    <Select.Option key={salaryPeriod.id} value={salaryPeriod.id}>
                      {formatDate(salaryPeriod.start)} - {formatDate(salaryPeriod.end)}
                    </Select.Option>
                  )
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )}
      <CoarseTimeRegistrationForm
        key={salaryPeriodID}
        employee={props.employee}
        salaryTypes={props.salaryTypes}
        workHours={getWorkHours()}
        workDays={getWorkDays()}
        period={period}
        workSchedule={contract.workSchedule}
        addAlert={props.addAlert}
        onSubmit={handleSubmit}
        selectedContractID={selectedContractID}
        loadedDelta={loadedDelta}
      />
      {(props.contracts.saving || props.coarseTimeRegistrations.saving) && (
        <LoadingOverlay
          text={formatSavingText([
            { loading: props.contracts.saving, text: t('loading.reducer.contracts') },
            { loading: props.coarseTimeRegistrations.saving, text: t('loading.reducer.coarse_time_registrations') },
          ])}
        />
      )}
    </>
  )
}
